/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { FC, useMemo, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'src/app/hooks';
import ChipSocialMediaObjectSelector from 'src/components/ChipSocialMediaObjectSelector';
import Empty from 'src/components/Empty';
import { IObject } from 'src/models/objectManagement';
import SearchIcon from '@mui/icons-material/Search';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useSelectItemDefaultSelected } from 'src/hooks/useSelectItemDefaultSelected';
import ModalAddKeywordAndHashtag from 'src/components/ModalAddKeywordAndHashtag';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { useLocation } from 'react-router';
import { t } from 'i18next'

interface Props {
  objectList: Array<IObject>;
  onParentVisible?: () => void;
  onSelect: (items: Array<string>) => void;
  objectListIdActive: Array<string>;
}

interface Item {
  name: string;
}

const SectionKeywordHashtag: FC<Props> = ({
  objectList,
  onParentVisible = () => { },
  onSelect,
  objectListIdActive
}) => {
  const theme = useTheme();

  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  const isEcommerce = pathname.includes('ecommerce') || tab === 'ecommerce';

  // Pagination state
  const ITEMS_PER_PAGE = 200;
  const [currentPage, setCurrentPage] = useState(1);
  const [isChangingPage, setIsChangingPage] = useState(false);

  const { isOnTour } = useAppSelector((state) => state.storeProductTour);

  // Refs for IntersectionObserver
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const addName = useMemo((): Array<IObject & Item> => {
    return objectList.map((object) => ({
      ...object,
      name: object.objectName
    }));
  }, [objectList]);

  const { filteredList: unsortedFilteredList, handleFilter } =
    useFiltersByName<IObject>(addName);

  const filteredList = useMemo(() => {
    if (!objectListIdActive.length) return unsortedFilteredList;
    if (!unsortedFilteredList.length) return [];

    const objectIdsSet = new Set(objectListIdActive);
    return unsortedFilteredList.sort((a, b) => {
      const aIsActive = objectIdsSet.has(a.id);
      const bIsActive = objectIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [unsortedFilteredList]); // add objectListIdActive as dependency if something went wrong

  // Calculate pagination data
  const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return isOnTour
      ? filteredList.slice(0, 3)
      : filteredList.slice(startIndex, endIndex);
  }, [filteredList, currentPage]);

  const { groupSelectedById } = useAppSelector(
    (state) => state.objectManagement
  );

  const { selectedItemByIds, handleClick, isSelected, handleSelectAll } =
    useSelectItemDefaultSelected<IObject>(filteredList, objectListIdActive);

  const { isCheckedAll, isIndeterminate } = useMemo(() => {
    const selectedIdsSet = new Set(selectedItemByIds);
    const checkedItemsCount = filteredList.filter((el) =>
      selectedIdsSet.has(el.id)
    ).length;
    const isCheckedAll = filteredList.length === checkedItemsCount;
    const isIndeterminate =
      checkedItemsCount > 0 && checkedItemsCount < filteredList.length;

    return { isCheckedAll, isIndeterminate };
  }, [filteredList, selectedItemByIds]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (isChangingPage) return;

    const container = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = container;

    const SCROLL_THRESHOLD = 50;

    // Scroll to bottom - next page
    if (
      scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD &&
      hasNextPage
    ) {
      // console.log('Triggering next page via scroll');
      setIsChangingPage(true);
      setCurrentPage((prev) => prev + 1);

      // Reset scroll to MIDDLE of container to avoid immediate re-trigger
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          // Set scroll to middle of the container
          container.scrollTop = Math.max(100, container.scrollHeight * 0.3);
        }
        setIsChangingPage(false);
      }, 150);
    }

    // Scroll to top - previous page
    else if (scrollTop <= SCROLL_THRESHOLD && hasPreviousPage) {
      // console.log('Triggering previous page via scroll');
      setIsChangingPage(true);
      setCurrentPage((prev) => prev - 1);

      // Reset scroll to MIDDLE-BOTTOM of container to avoid immediate re-trigger
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          // Set scroll to 70% down the container
          container.scrollTop = Math.max(100, container.scrollHeight * 0.7);
        }
        setIsChangingPage(false);
      }, 150);
    }
  };

  // Reset to page 1 when filteredList changes (e.g., after search)
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredList]);

  useEffect(() => {
    onSelect(selectedItemByIds);
  }, [selectedItemByIds]);

  return (
    <>
      <DialogTitle
        sx={{
          m: theme.spacing(3, 3, 1),
          p: theme.spacing(2, 3, 0, 3)
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3">
            {isEcommerce ? t('keyword') : t('keyword & hashtag')}
          </Typography>
          {groupSelectedById === '_default' ? (
            <ModalAddKeywordAndHashtag
              title={isEcommerce ? t('keyword') : t('keyword & hashtag')}
              onHide={onParentVisible}
              objectList={objectList}
            />
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          m: theme.spacing(1, 3)
        }}
      >
        <Stack direction="row" spacing={2} sx={{ mb: theme.spacing(1) }}>
          <Box sx={{ minWidth: theme.spacing(15) }}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<FilterNoneIcon />}
                  checkedIcon={<LibraryAddCheckIcon />}
                  indeterminateIcon={<IndeterminateCheckBoxIcon />}
                  indeterminate={isIndeterminate}
                />
              }
              label={t('select all')}
              onChange={handleSelectAll}
              checked={isCheckedAll}
            />
          </Box>
          <TextField
            fullWidth
            id="standard-basic"
            placeholder={t('search profile')}
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{
              mb: theme.spacing(3)
            }}
            onChange={(e) => handleFilter(e.target.value)}
          />
        </Stack>
        {/* Scrollable container */}
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          sx={{
            height: 104,
            overflow: 'auto',
            // border: `1px solid ${theme.palette.divider}`,
            // borderRadius: 1,
            backgroundColor: theme.palette.background.default
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              // gap: 1,
              // p: 2,
              minHeight: '100%'
            }}
          >
            {/* Loading indicator for top */}
            {/* {hasPreviousPage && (
              <Box
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  py: 1,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  opacity: isChangingPage ? 0.7 : 0.4
                }}
              >
                ↑ Scroll up to load previous page
              </Box>
            )} */}
            {paginatedItems.length > 0 ? (
              paginatedItems.map(
                ({
                  id,
                  content,
                  objectName,
                  socialMedia,
                  isHashtag,
                  streamType
                }) => {
                  const isItemSelected = !isSelected(id);
                  return (
                    <ChipSocialMediaObjectSelector
                      key={`${id}${content}`}
                      id={content}
                      name={objectName}
                      socialMedia={socialMedia}
                      streamType={isHashtag ? 'hashtag' : streamType}
                      // sx={{ m: '0 8px 8px 0' }}
                      onClick={(event) => handleClick(event, id)}
                      checked={isItemSelected}
                    />
                  );
                }
              )
            ) : (
              <Empty />
            )}
            {/* Loading indicator for bottom */}
            {/* {hasNextPage && (
              <Box
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  py: 1,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  opacity: isChangingPage ? 0.7 : 0.4
                }}
              >
                ↓ Scroll down to load next page
              </Box>
            )} */}
          </Box>
        </Box>
      </DialogContent>
    </>
  );
};

export default SectionKeywordHashtag;
